<template>
  <div
    class="playlist-layer playlist-item numbered-item graphic graphics-group"
    :class="{'editing': isEditing}"
    @click.prevent="!isEditingName && onClickTimelineItem()"
    v-click-outside="saveName"
  >
    <div class="layer-info" @dblclick.prevent="!isEditingName ? toggleCollapse() : null">
      <button
        class="drag-handle tb-btn tb-btn-link sm low-em"
        title="Rearrange"
      >
        <span class="fas fa-grip-vertical"></span>
      </button>

      <span class="playlist-item-number" aria-hidden="true" />

      <div class="layer-title">
        <button
          class="tb-btn tb-btn-link sm low-em collapse-btn"
          :class="isCollapsed ? 'collapse-btn--rotated' : ''"
          :title="isCollapsed ? 'Expand' : 'Collapse'"
          @click.stop="toggleCollapse"
        >
          <span class="fas fa-chevron-down"></span>
        </button>

        <div class="layer-name--editable gap-0">
          <div class="layer-name--editable__main">
            <span v-if="!isEditingName" class="far fa-image-stack"></span>
            <div
              v-if="!isEditingName"
              :title="title"
              class="title-text"
              @dblclick.stop="editName"
            >
              {{ title }}
            </div>
            <span v-if="!isEditingName" @click.stop="editName" class="fas fa-pencil-alt"></span>
          </div>

          <div v-if="!isEditingName" class="description-text layer-name--editable__count">
            {{ graphicsCount }}
          </div>

          <div v-if="isEditingName" class="layer-name--editable__edit">
            <input
              type="text"
              class="layer-name__input min-w-[12rem] appearance-none"
              data-test="graphics-group-title-input"
              v-model="name"
              autofocus
              ref="nameInput"
              @keyup.enter="saveName"
              @keyup.esc="cancelEditingName"
            />
            <span
              @click.stop="saveName"
              class="fas fa-check layer-name__action layer-name__action--accept"
              data-test="graphics-group-title-accept"
            />
            <span
              @click.stop="cancelEditingName"
              class="fas fa-times layer-name__action layer-name__action--cancel"
              data-test="graphics-group-title-cancel"
            />
          </div>
        </div>
      </div>

      <div class="layer-description capitalize">
        <duration
          v-if="showDuration"
          v-model:duration="item.displayTime"
        />
      </div>

      <preview-live-switch :item='item'></preview-live-switch>

      <dropdown position="left" class="more-actions ml-1" @dblclick.stop>
        <template #trigger>
          <button
            class="tb-btn tb-btn-text sm medium-em"
            title="More actions"
            @dblclick.stop
          >
            <span class="fas fa-ellipsis-v"></span>
          </button>
        </template>
        <template #dropdown-body>
          <button
            class="dropdown-list-item"
            v-on:click="duplicateItem"
          >
            <span class="dropdown-list-item-label">Duplicate in Playlist</span>
          </button>

          <button
            v-if="canDuplicateInGroup"
            class="dropdown-list-item"
            v-on:click="duplicateItemInGroup"
          >
            <span class="dropdown-list-item-label">Duplicate in Scene</span>
          </button>

          <button
            v-if="canMergeUp"
            class="dropdown-list-item"
            @click="mergeUp"
            title="Merge with above graphic or group in the playlist"
          >
            <span class="dropdown-list-item-label">Merge Up</span>
          </button>

          <button
            v-if="canMergeDown"
            class="dropdown-list-item"
            @click="mergeDown"
            title="Merge with below graphic or group in the playlist"
          >
            <span class="dropdown-list-item-label">Merge Down</span>
          </button>

          <hr>

          <button
            class="dropdown-list-item"
            v-on:click="removeItem"
          >
            <span class="dropdown-list-item-label warning">
              Remove
            </span>
          </button>
        </template>
      </dropdown>
    </div>

      <div
      v-if="!isCollapsed"
      class="position-relative"
      :class="item.posts.length ? '' : 'py-2 '"
    >
      <draggable
        v-model="item.posts"
        class="playlist-draggable playlist-draggable--nested tgbDropArea tgbDropArea--graphics"
        :class="[{'empty': !item.posts.length},'zoom-' + zoom]"
        :move="onGroupMove"
        v-bind="{
          group: { name: 'timeline', pull: true, put: allowedSourceGroups },
          animation: 200,
          touchStartThreshold: 10,
        }"
        handle=".drag-handle"
        revert-on-spill="true"
        data-test="producer-timeline-graphicGroup-droparea"
        @change="onChangeGroup"
      >
        <component
          v-for="child in children"
          class="playlist-layer--nested"
          :key="child.timeline_id"
          :data-timeline-id="child.timeline_id"
          :is="getType(child)"
          :root-item="item"
          :item="child"
          :grouped="true"
          :in-container="true"
          :container-timeline-id="item.timeline_id"
          @queued-post="onQueuePost(child)"
        />

        <div class="tgbDropAreaHelp" v-if="!item.posts.length">
          <p>Drag and drop Custom Graphics here</p>
        </div>
      </draggable>
    </div>
  </div>
</template>

<script>
import ConduitEventsService from '../../../services/conduitEventsService';
import { getSocialPosts } from '../../../utils/production';

import TimelineMixins from '../../../mixins/timeline-mixins';
import DurationComponent from '../duration-input/DurationInput.vue';
import PreviewLiveSwitch from '../../PreviewLiveSwitch.vue';
import TimelineMarker from '../TimelineMarker.vue';

import GraphicComponent from './TimelineGraphic.vue';

const dispatchEvent = (event) => {
  const evt = document.createEvent('Event');
  evt.initEvent(event, true, true);
  document.dispatchEvent(evt);
};

export default {
  name: 'TimelineGraphicsComponent',
  mixins: [TimelineMixins],

  props: {
    inContainer: {
      type: Boolean,
      default: false,
    },

    containerTimelineId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      isEditingName: false,
      isCollapsed: false,
    };
  },

  components: {
    duration: DurationComponent,
    'preview-live-switch': PreviewLiveSwitch,
    graphic: GraphicComponent,
    'timeline-marker': TimelineMarker,
  },

  computed: {
    allowedSourceGroups() {
      // Allow graphics and markers in groups.
      return (to, from, dragEl) => (
        this.$store.state.drag.type === 'graphic'
        || this.$store.state.drag.type === 'marker'
        || dragEl?.dataset?.componentType === 'graphic'
        || dragEl?.dataset?.componentType === 'marker'
      );
    },

    /**
    * Groups only have a duration if there's NO social graphics inside of them.
    * If there's a social graphic with individual posts, then time is determined by those.
    * @returns {Boolean}
    */
    showDuration() {
      const socialPosts = getSocialPosts(this.item);

      // Show duration if there's no social posts inside the group
      if (!socialPosts?.length) {
        return true;
      }

      // Also show duration if the only socialPosts are tagboards, not posts
      if (socialPosts.some((post) => post?.type === 'tagboard')) {
        return true;
      }

      // Otherwise no duration (i.e. has social posts)
      return false;
    },

    /**
     * Disable nested draggable if user is dragging a non-graphic
     */
    isDraggingDisabled() {
      const { type } = this.$store.state.drag;
      return type && type !== 'graphic';
    },

    graphics() {
      return this.item.posts;
    },

    children() {
      return this.item.posts || [];
    },

    graphicsCount() {
      const label = 'Custom Graphic';
      return `${this.graphics.length} ${label}${this.graphics.length === 1 ? '' : 's'}`;
    },

    title() {
      return this.item.title;
    },
  },
  methods: {
    /**
     * Validate drops before Sortable commits the move. Rejecting an invalid
     * drop here (instead of letting it land and then reverting) stops Sortable
     * from physically moving the DOM node and leaving a stray element behind.
     * Graphics groups only accept custom graphics (and markers).
     * @param {Object} evt - Sortable move event carrying draggedContext
     * @returns {Boolean} Whether the drop is allowed
     */
    onGroupMove(evt) {
      const element = evt?.draggedContext?.element;

      if (!element?.layout) {
        return false;
      }

      return ['graphic', 'marker'].includes(element.layout);
    },

    getType(item) {
      if (item?.layout === 'marker') {
        return 'timeline-marker';
      }

      return 'graphic';
    },

    /**
    * Called whenever a social post is queued in an inner graphic.
    * Used to clear the queued post on other graphics in the group.
    */
    onQueuePost(graphic) {
      this.item.posts.forEach((post) => {
        if (post.timeline_id === graphic.timeline_id) {
          // Ignore the graphic with the new queued post
          return;
        }

        const [socialId] = post.meta?.typeLookup?.['social-data'] || [];
        if (socialId && post.fields[socialId]) {
          // If post has a social element, then remove the queued post
          // eslint-disable-next-line no-param-reassign
          post.fields[socialId].queuedPost = null;
        }
      });
    },

    onChangeGroup(e) {
      if (e.added) {
        const { element, newIndex } = e.added;
        const { group } = this.$store.state.drag;

        // Clear queuedPost on social graphics when added to the group
        const [id] = element.meta?.typeLookup?.['social-data'] || [];
        if (id) {
          const fields = element.fields[id];
          if (fields) {
            fields.queuedPost = null;
          }
        }

        // Default new graphics to hidden when the group is the current live element,
        // so they don't immediately appear in the output
        const isGroupLive = this.isCurrentLayout || this.isCurrentSampleLayout;

        // This should be handled with global state,
        // but we'll just let the tabs know to clear for now
        dispatchEvent('clearTabSelection');

        if (group && group.length) {
          // Make sure all items in drag group are graphics, ignoring all the rest.
          const items = group.filter((item) => item.layout === 'graphic');
          const excludedItems = group.filter((item) => item.layout !== 'graphic');

          if (excludedItems.length) {
            const groupNoun = 'Only Custom Graphics are allowed in a Custom Scene.';
            this.warningToast(`${excludedItems.length} item${excludedItems.length > 1 ? 's' : ''} excluded. ${groupNoun}`);
          }

          if (items.length) {
            // Add entire group to the graphics group
            this.$store.dispatch('replaceGroupItem', {
              groupIndex: this.index,
              index: newIndex,
              items,
            });

            // Mark the inserted graphics as hidden through the store when the
            // group is live, so they don't immediately appear in the output
            items.forEach((item) => {
              this.$store.dispatch('setTimelineItemHidden', {
                timelineId: item.timeline_id,
                hidden: isGroupLive,
              });
            });
          }
        } else {
          // Single item: mark it hidden through the store when the group is
          // live, so it doesn't immediately appear in the output
          this.$store.dispatch('setTimelineItemHidden', {
            timelineId: element.timeline_id,
            hidden: isGroupLive,
          });

          // Deterministically place the dragged item in THIS group and
          // nowhere else (nested-list drags can leave it duplicated).
          this.$store.dispatch('moveTimelineItemsToContainer', {
            items: [element],
            containerTimelineId: this.item.timeline_id,
            index: newIndex,
          });
        }

        ConduitEventsService.track(
          'producer.dragged.graphics-to-group',
          {
            production_id: this.production._id,
            count: group?.length || 1,
            graphics: (group?.length) ? group : [element],
            graphicsGroup: this.item,
          },
          {
            ownerId: this.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
        );
      }

      // Clean up any null/empty entries vuedraggable may have left in the posts array
      // This can happen when items are dragged between nested draggable lists
      // and the operation is reverted or encounters a Vue reactivity edge case.
      // Empty `{}` objects are dropped too: they're corrupt and break rendering.
      if (e.removed || e.moved) {
        this.$store.dispatch('cleanContainerPosts', {
          containerTimelineId: this.item.timeline_id,
        });
      }

      // Make sure metadata is fetched for any new timeline items missing from lookup tables
      this.$store.dispatch('getTimelineItems');
      this.$store.dispatch('trackSportradarInGameElement');

      // Always send updateTimeline event so other connected clients see change
      this.$store.dispatch('updateTimeline', { force: true });
      this.cleanupStrayNodes();

      if (this.isCurrentSampleLayout) {
        // Re-render preview
        this.$store.dispatch('updatePreviewTimeline', { force: true });
      }
    },

    /**
     * Remove any DOM nodes Sortable left inside this group's dropzone that no
     * longer correspond to a graphic. Nested-list drags (root <-> group) can
     * leave a stray moved node behind after a reverted/desynced drop, and
     * because Vue doesn't manage those nodes it never removes them.
     */
    cleanupStrayNodes() {
      this.$nextTick(() => {
        const container = this.$el?.querySelector('.playlist-draggable--nested');
        if (!container) {
          return;
        }

        const validIds = new Set((this.item.posts || []).map((post) => post?.timeline_id));

        Array.from(container.children).forEach((child) => {
          const timelineId = child.getAttribute?.('data-timeline-id');
          if (timelineId && !validIds.has(timelineId)) {
            child.remove();
          }
        });
      });
    },

    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },

    editName() {
      this.name = this.item.title;
      this.isEditingName = true;

      this.$nextTick(() => {
        // Autofocus and highlight all text
        this.$refs.nameInput.focus();
        this.$refs.nameInput.select();
      });
    },

    cancelEditingName() {
      this.isEditingName = false;
    },

    saveName() {
      if (!this.isEditingName) { return; }

      this.isEditingName = false;

      // User left the name blank, so revert back to original name
      if (!this.name || !this.name.trim()) { return; }

      // Sucks updating this directly here,
      // we should probably issue a proper state change through Vuex
      this.item.title = this.name.trim();

      // Call updateTimeline to let other connected clients see the name change
      this.$store.dispatch('updateTimeline');

      ConduitEventsService.track(
          'producer.renamed.group',
          {
            production_id: this.production._id,
            name: this.item.title,
          },
          {
            ownerId: this.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );
    },
  },
};
</script>

<style scoped lang="scss">
.layer-name__input {
  padding: 0.45rem 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  background-color: rgba(255, 255, 255, 0.08);
  color: #e1e1e1;
  box-shadow: none;
  outline: none;

  &:focus {
    border-color: rgba(255, 255, 255, 0.2);
    background-color: rgba(255, 255, 255, 0.12);
  }
}

.layer-name__action {
  cursor: pointer;
  font-size: 0.875rem;
}

.layer-name__action--accept {
  color: #00d483;
}

.layer-name__action--cancel {
  color: #e65447;
}
</style>
